import type { TFunction } from 'i18next'

import {
  countriesEnum,
  languagesEnum,
  domainRankEnum,
  typeMovementEnum,
  styleArticleEnum,
  paymentStatusEnum,
  paymentMethodEnum,
  articleStatusEnum,
  invoiceStatusEnum,
  teamMembersStatusEnum,
  guestPostingStatusEnum,
  copyrightImageSourceEnum,
  guestPostingPublicationStepsEnum,
  languagesFilterEnum,
  publicPromptEnum
} from '@dr-keyword-commons/enums'

import { userRolesEnum } from '@dr-keyword-commons/enums'

export const formatTypeMovements = (t: TFunction) => ({
  [typeMovementEnum.RECHARGE]: t('formatters.typeMovements.recharge'),
  [typeMovementEnum.GUESTPOSTING]: t('formatters.typeMovements.guestPosting')
})

export const formatTeamMemberStatus = (t: TFunction) => ({
  [teamMembersStatusEnum.ACTIVE]: t('formatters.teamMemberStatus.active'),
  [teamMembersStatusEnum.DEACTIVATED]: t(
    'formatters.teamMemberStatus.deactivated'
  ),
  [teamMembersStatusEnum.INVITATION_PENDING]: t(
    'formatters.teamMemberStatus.invitationPending'
  )
})

export const formatLanguage = (t: TFunction) => ({
  [languagesEnum.PT]: t('formatters.language.pt'),
  [languagesEnum.ENGLISH]: t('formatters.language.english'),
  [languagesEnum.SPANISH]: t('formatters.language.spanish')
})

export const formatLanguageByShortCode = (t: TFunction) => ({
  [languagesFilterEnum.PT]: t('formatters.language.pt'),
  [languagesFilterEnum.ENGLISH]: t('formatters.language.english'),
  [languagesFilterEnum.SPANISH]: t('formatters.language.spanish')
})

export const formatStyleArticle = (t: TFunction) => ({
  [styleArticleEnum.BLOG]: t('formatters.styleArticle.blog'),
  [styleArticleEnum.FORMAL]: t('formatters.styleArticle.formal'),
  [styleArticleEnum.NATURAL]: t('formatters.styleArticle.natural'),
  [styleArticleEnum.INFORMAL]: t('formatters.styleArticle.informal'),
  [styleArticleEnum.NEWSCAST]: t('formatters.styleArticle.newscast')
})

export const formatUserRole = (t: TFunction) => ({
  [userRolesEnum.STAFF]: t('formatters.userRole.staff'),
  [userRolesEnum.WRITER]: t('formatters.userRole.writer'),
  [userRolesEnum.OWNER]: t('formatters.userRole.owner'),
  [userRolesEnum.READER]: t('formatters.userRole.reader'),
  [userRolesEnum.SUPERVISOR]: t('formatters.userRole.supervisor'),
  [userRolesEnum.ORG_ADMIN]: t('formatters.userRole.orgAdmin')
})

export const formatGuestPostingStatus = (t: TFunction) => ({
  [guestPostingStatusEnum.DRAFT]: t('formatters.guestPostingStatus.draft'),

  [guestPostingStatusEnum.DELETED]: t('formatters.guestPostingStatus.deleted'),

  [guestPostingStatusEnum.PUBLISHED]: t(
    'formatters.guestPostingStatus.published'
  ),
  [guestPostingStatusEnum.COMPOSING]: t(
    'formatters.guestPostingStatus.composing'
  ),
  [guestPostingStatusEnum.PUBLISHING]: t(
    'formatters.guestPostingStatus.publishing'
  ),
  [guestPostingStatusEnum.UNPUBLISHING]: t(
    'formatters.guestPostingStatus.unpublishing'
  ),
  [guestPostingStatusEnum.PAYMENT_FAILED]: t(
    'formatters.guestPostingStatus.paymentFailed'
  ),
  [guestPostingStatusEnum.PUBLISH_FAILED]: t(
    'formatters.guestPostingStatus.publishFailed'
  ),
  [guestPostingStatusEnum.COMPOSING_FAIL]: t(
    'formatters.guestPostingStatus.composingFail'
  )
})

export const formatArticleStatus = (t: TFunction) => ({
  [articleStatusEnum.PUBLISH]: t('formatters.articleStatus.publish'),
  [articleStatusEnum.PENDING]: t('formatters.articleStatus.pending')
})

export const formatCountries = (t: TFunction) => ({
  [countriesEnum.PERU]: t('formatters.countries.peru'),
  [countriesEnum.SPAIN]: t('formatters.countries.spain'),
  [countriesEnum.CHILE]: t('formatters.countries.chile'),
  [countriesEnum.MEXICO]: t('formatters.countries.mexico'),
  [countriesEnum.BRAZIL]: t('formatters.countries.brazil'),
  [countriesEnum.CANADA]: t('formatters.countries.canada'),
  [countriesEnum.PANAMA]: t('formatters.countries.panama'),
  [countriesEnum.ECUADOR]: t('formatters.countries.ecuador'),
  [countriesEnum.BOLIVIA]: t('formatters.countries.bolivia'),
  [countriesEnum.URUGUAY]: t('formatters.countries.uruguay'),
  [countriesEnum.USA]: t('formatters.countries.unitedStates'),
  [countriesEnum.PARAGUAY]: t('formatters.countries.paraguay'),
  [countriesEnum.PORTUGAL]: t('formatters.countries.portugal'),
  [countriesEnum.COLOMBIA]: t('formatters.countries.colombia'),
  [countriesEnum.HONDURAS]: t('formatters.countries.honduras'),
  [countriesEnum.VENEZUELA]: t('formatters.countries.venezuela'),
  [countriesEnum.ARGENTINA]: t('formatters.countries.argentina'),
  [countriesEnum.GUATEMALA]: t('formatters.countries.guatemala'),
  [countriesEnum.NICARAGUA]: t('formatters.countries.nicaragua'),
  [countriesEnum.COSTA_RICA]: t('formatters.countries.costaRica'),
  [countriesEnum.DOMINICANA]: t('formatters.countries.dominicana'),
  [countriesEnum.PUERTO_RICO]: t('formatters.countries.puertoRico'),
  [countriesEnum.EL_SALVADOR]: t('formatters.countries.elSalvador')
})

export const countriesList = (t: TFunction) =>
  Object.values(countriesEnum)
    .map((country) => ({
      value: country,
      label: formatCountries(t)[country]
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

export const formatDomainRank = (t: TFunction) => ({
  [domainRankEnum.FROM_0_20]: t('formatters.domainRank.from0_20'),
  [domainRankEnum.FROM_20_30]: t('formatters.domainRank.from20_30'),
  [domainRankEnum.FROM_30_40]: t('formatters.domainRank.from30_40'),
  [domainRankEnum.MORE_40]: t('formatters.domainRank.more40')
})

export const formatInvoiceStatus = (t: TFunction) => ({
  [invoiceStatusEnum.PAID]: t('formatters.invoiceStatus.paid'),

  [invoiceStatusEnum.FAILED]: t('formatters.invoiceStatus.failed'),

  [invoiceStatusEnum.PENDING]: t('formatters.invoiceStatus.pending'),

  [invoiceStatusEnum.NOT_PAID]: t('formatters.invoiceStatus.notPaid'),

  [invoiceStatusEnum.REFUNDED]: t('formatters.invoiceStatus.refunded'),

  [invoiceStatusEnum.PAYMENT_ERROR]: t('formatters.invoiceStatus.paymentError'),

  [invoiceStatusEnum.PAYMENT_CONFLICT]: t(
    'formatters.invoiceStatus.paymentConflict'
  ),
  [invoiceStatusEnum.REFUND_REQUESTED]: t(
    'formatters.invoiceStatus.refundRequested'
  ),
  [invoiceStatusEnum.REFUND_CONFLICT]: t(
    'formatters.invoiceStatus.refundConflict'
  )
})

export const formatPaymentMethod = (t: TFunction) => ({
  [paymentMethodEnum.STRIPE]: t('formatters.paymentMethod.stripe'),
  [paymentMethodEnum.PAYPAL]: t('formatters.paymentMethod.paypal'),
  [paymentMethodEnum.WALLET]: t('formatters.paymentMethod.wallet')
})

export const formatImageSource = (t: TFunction) => ({
  [copyrightImageSourceEnum.MADE_BY_ME]: t('formatters.imageSource.madeByMe'),

  [copyrightImageSourceEnum.GOOGLE_IMAGES]: t(
    'formatters.imageSource.googleImages'
  ),
  [copyrightImageSourceEnum.SOCIAL_NETWORKS]: t(
    'formatters.imageSource.socialNetworks'
  ),
  [copyrightImageSourceEnum.PAID_IMAGE_BANK]: t(
    'formatters.imageSource.paidImageBank'
  ),
  [copyrightImageSourceEnum.FREE_IMAGE_BANK]: t(
    'formatters.imageSource.freeImageBank'
  ),
  [copyrightImageSourceEnum.MADE_BY_A_PROFESSIONAL]: t(
    'formatters.imageSource.madeByAProfessional'
  ),
  [copyrightImageSourceEnum.AI_GENERATED]: t(
    'formatters.imageSource.aiGenerated'
  )
})

export const formatSteps = {
  [guestPostingPublicationStepsEnum.REVISION]: 'revisar',
  [guestPostingPublicationStepsEnum.RESULTS]: 'resultados',
  [guestPostingPublicationStepsEnum.COMBINATION]: 'combinacion',
  [guestPostingPublicationStepsEnum.REDACTION]: 'editar?editor=true',
  [guestPostingPublicationStepsEnum.QUICK_CAMPAIGN]: 'campana-rapida',
  [guestPostingPublicationStepsEnum.GENERATION]: 'editar?withIa=true',
  [guestPostingPublicationStepsEnum.PUBLICATION_PAYMENTS]: 'publicacion'
}

export const formatPaymentStatus = (t: TFunction) => ({
  [paymentStatusEnum.PAID]: t('formatters.paymentStatus.paid'),

  [paymentStatusEnum.FAILED]: t('formatters.paymentStatus.failed'),

  [paymentStatusEnum.PENDING]: t('formatters.paymentStatus.pending'),

  [paymentStatusEnum.CANCELLED]: t('formatters.paymentStatus.cancelled'),

  [paymentStatusEnum.REFUNDED]: t('formatters.paymentStatus.refunded'),

  [paymentStatusEnum.REFUND_REQUESTED]: t(
    'formatters.paymentStatus.refundRequested'
  ),
  [paymentStatusEnum.REFUND_CONFLICT]: t(
    'formatters.paymentStatus.refundConflict'
  )
})

export const formatPublicPrompts = (t: TFunction) => ({
  [publicPromptEnum.SIMPLIFY]: t('formatters.publicPrompts.simplify'),
  [publicPromptEnum.SUMMARIZE]: t('formatters.publicPrompts.summarize'),
  [publicPromptEnum.TAKEAWAYS]: t('formatters.publicPrompts.takeaways'),
  [publicPromptEnum.MAKE_LONGER]: t('formatters.publicPrompts.make_longer'),
  [publicPromptEnum.MAKE_SHORTER]: t('formatters.publicPrompts.make_shorter'),
  [publicPromptEnum.FIX_SPELLING]: t('formatters.publicPrompts.fix_spelling'),
  [publicPromptEnum.IMPROVE_WRITING]: t(
    'formatters.publicPrompts.improve_writing'
  ),
  [publicPromptEnum.CHANGE_TONE_CONFIDENT]: t(
    'formatters.publicPrompts.change_tone_confident'
  ),
  [publicPromptEnum.CHANGE_TONE_PROFESSIONAL]: t(
    'formatters.publicPrompts.change_tone_professional'
  )
})
